import { AlertCircle, RefreshCw, X, ShoppingCart } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { useT } from '@/app/i18n/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import type { ProductCurrencyType } from '@/entities/product/types';
import type { BookedActivity, BookedRoom, BookedSlot } from '@/entities/room/types';
import { useCurrency } from '@/features/product/contexts/CurrencyContext';
import ProductHtml from '@/features/product/ui/Html';
import { utilDateFormat } from '@/lib/date';
import { cn, utilFromMinorUnit, utilGetAdditionalCount } from '@/lib/utils';
import { useProductStore } from '@/stores/product';

interface CartSectionProps {
  currency: ProductCurrencyType;
  bookedRooms: BookedRoom[];
  bookedActivities: BookedActivity[];
  bookedSlots: BookedSlot[];
  totalAmount: number;
  cancellationPolicy: string;
  onRemoveRoom: (index: number) => void;
  onBookNow: () => void;
  onRemoveActivity: (index: number) => void;
  onRemoveSlot: (index: number) => void;
}

function CartContent({
  currency,
  bookedRooms,
  bookedActivities,
  bookedSlots,
  totalAmount,
  cancellationPolicy,
  onRemoveRoom,
  onRemoveActivity,
  onRemoveSlot,
  onBookNow,
}: CartSectionProps) {
  const { t } = useT('product');

  return (
    <>
      <div
        className={cn(
          (bookedRooms.length > 0 || bookedActivities.length > 0 || bookedSlots.length > 0) &&
            'max-h-[63vh] space-y-10 overflow-y-auto px-5 max-lg:px-0',
        )}
      >
        {/* Booked Rooms */}
        {bookedRooms.length > 0 && (
          <div className='space-y-5'>
            {bookedRooms.map((room, index) => (
              <div key={index}>
                <h3 className='line-clamp-1 text-base font-bold text-[#2E2E2E]'>{room.name}</h3>
                <div className='flex items-center justify-between gap-4'>
                  <div>
                    {room?.checkIn && room?.checkOut && (
                      <p className='mb-2 text-xs font-medium text-[#0064FF]'>
                        {utilDateFormat(room.checkIn, 'MM.DD')} ~{' '}
                        {utilDateFormat(room.checkOut, 'MM.DD')}
                      </p>
                    )}

                    <p className='mb-1 line-clamp-1 text-sm text-gray-600'>
                      {t('guest')}{' '}
                      {utilGetAdditionalCount(room.standardPeople + room.addPeopleQuantity)}
                    </p>

                    {room?.option && (
                      <p className='mb-1 line-clamp-1 text-sm text-gray-600'>{room.option.title}</p>
                    )}
                  </div>
                  <div className='mt-auto flex gap-2'>
                    <p className='min-w-[125px] text-right text-base font-bold text-[#0064FF]'>
                      {currency} {utilFromMinorUnit(room.totalAmount, currency)}
                    </p>
                    <button
                      onClick={() => onRemoveRoom(index)}
                      className='text-red-500 transition-colors hover:text-red-700'
                    >
                      <X className='h-5 w-5' />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Booked Activities */}
        {bookedActivities.length > 0 && (
          <div className='space-y-5'>
            {bookedActivities.map((activity, index) => (
              <div key={`${activity.title}-${index}`}>
                <h3 className='line-clamp-1 text-base font-bold text-[#2E2E2E]'>
                  {activity.title} {utilGetAdditionalCount(activity.quantity, true)}
                </h3>
                <div className='flex items-center justify-between gap-4'>
                  <div>
                    {activity?.date && (
                      <p className='mb-2 text-xs font-medium text-[#0064FF]'>
                        {utilDateFormat(activity.date, 'MM.DD')}
                      </p>
                    )}
                    {activity.optionGroupList.map(optionGroup =>
                      optionGroup.optionList.map(option => (
                        <p key={option.idx} className='mb-1 line-clamp-1 text-sm text-gray-600'>
                          {option.title} {utilGetAdditionalCount(activity.quantity, true)}
                        </p>
                      )),
                    )}
                  </div>

                  <div className='mt-auto flex gap-2'>
                    <p className='min-w-[125px] text-right text-base font-bold text-[#0064FF]'>
                      {currency} {utilFromMinorUnit(activity.totalAmount, currency)}
                    </p>
                    <button
                      onClick={() => onRemoveActivity(index)}
                      className='text-red-500 transition-colors hover:text-red-700'
                    >
                      <X className='h-5 w-5' />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Booked Slots */}
        {bookedSlots.length > 0 && (
          <div className='space-y-5'>
            {bookedSlots.map((slot, index) => (
              <div key={`${slot.title}-${index}`}>
                <h3 className='line-clamp-1 text-base font-bold text-[#2E2E2E]'>
                  {slot.title} {utilGetAdditionalCount(slot.slotOptionQuantity, true)}
                </h3>
                <div className='flex items-center justify-between gap-4'>
                  <div>
                    {slot?.date && (
                      <p className='mb-1 text-xs font-medium text-[#0064FF]'>
                        {utilDateFormat(slot.date, 'MM.DD')}
                      </p>
                    )}
                    {slot.slotOptionList?.[0] && (
                      <p className='mb-1 line-clamp-1 text-xs text-gray-600'>
                        {slot.slotOptionList[0].title}
                      </p>
                    )}
                    {slot.optionGroupList.map(optionGroup =>
                      optionGroup.optionList.map(option => (
                        <p key={option.idx} className='mb-1 line-clamp-1 text-xs text-gray-600'>
                          {option.title}
                        </p>
                      )),
                    )}
                  </div>

                  <div className='mt-auto flex gap-2'>
                    <p className='text-right text-base font-bold text-[#0064FF]'>
                      {currency} {utilFromMinorUnit(slot.totalAmount, currency)}
                    </p>
                    <button
                      onClick={() => onRemoveSlot(index)}
                      className='text-red-500 transition-colors hover:text-red-700'
                    >
                      <X className='h-5 w-5' />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {(bookedRooms.length > 0 || bookedActivities.length > 0 || bookedSlots.length > 0) && (
        <div className='mt-6 border-t border-[#E9ECEF] px-5 pt-6 max-lg:px-0'>
          <div className='mb-6 flex flex-wrap items-center justify-between gap-2 max-sm:mb-5 max-sm:flex-col max-sm:items-end max-sm:gap-1'>
            <span className='text-xl font-bold text-[#2E2E2E] max-sm:text-base'>
              {t('totalAmount')}
            </span>
            <span className='ml-auto text-right text-2xl font-bold text-[#0064FF]'>
              {currency} {utilFromMinorUnit(totalAmount, currency)}
            </span>
          </div>
          {cancellationPolicy && (
            <div className='mb-4 flex items-center justify-end max-sm:mb-5 max-sm:flex-col max-sm:items-end max-sm:gap-1'>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant='link'
                    className='h-auto p-0 text-xs font-medium text-gray-400 underline decoration-gray-400 underline-offset-4'
                  >
                    {t('viewCancellationPolicy')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('cancellationPolicy')}</DialogTitle>
                  </DialogHeader>
                  <div className='max-h-[60vh] overflow-y-auto whitespace-pre-wrap text-sm'>
                    <ProductHtml html={cancellationPolicy} />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <Button
            className='w-full rounded-2xl bg-[#0064FF] px-7 py-6 text-lg font-bold text-white hover:bg-[#0052CC] max-sm:text-base'
            onClick={onBookNow}
          >
            {t('bookNow')}
          </Button>
        </div>
      )}
      {bookedRooms.length === 0 && bookedActivities.length === 0 && bookedSlots.length === 0 && (
        <div className='flex flex-col items-center justify-center gap-4 py-12 text-center'>
          <ShoppingCart className='h-10 w-10 text-gray-500' />
          <p className='text-base text-gray-500'>{t('yourCartEmpty')}</p>
        </div>
      )}
    </>
  );
}

export default function ProductCartSection({ cancellationPolicy }: { cancellationPolicy: string }) {
  const router = useRouter();
  const { t } = useT('product');

  const currency = useCurrency();

  const bookedRooms = useProductStore(state => state.bookedRooms);
  const bookedActivities = useProductStore(state => state.bookedActivities);
  const bookedSlots = useProductStore(state => state.bookedSlots);
  const setBookedRooms = useProductStore(state => state.setBookedRooms);
  const setBookedActivities = useProductStore(state => state.setBookedActivities);
  const setBookedSlots = useProductStore(state => state.setBookedSlots);
  const setCurrency = useProductStore(state => state.setCurrency);

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  const totalAmount = useMemo((): number => {
    return (
      bookedRooms.reduce((acc, room) => acc + room.totalAmount, 0) +
      bookedActivities.reduce((acc, activity) => acc + activity.totalAmount, 0) +
      bookedSlots.reduce((acc, slot) => acc + slot.totalAmount, 0)
    );
  }, [bookedRooms, bookedActivities, bookedSlots]);

  const onBookNow = (): void => {
    window.history.replaceState(null, '', window.location.pathname);
    setCurrency(currency);
    router.push('/checkout');
  };

  const onRemoveRoom = (index: number): void => {
    setBookedRooms(bookedRooms.filter((_, i) => i !== index));
  };

  const onRemoveActivity = (index: number): void => {
    setBookedActivities(bookedActivities.filter((_, i) => i !== index));
  };

  const onRemoveSlot = (index: number): void => {
    setBookedSlots(bookedSlots.filter((_, i) => i !== index));
  };

  const cartCount = bookedRooms.length + bookedActivities.length + bookedSlots.length;

  return (
    <>
      {/* sm 이상: 기존 UI */}
      <aside
        aria-label='장바구니'
        className='sticky top-8 h-fit rounded-3xl border border-[#E9ECEF] bg-white py-5 max-lg:hidden'
      >
        <ErrorBoundary FallbackComponent={CartFallbackComponentDesktop}>
          <CartContent
            currency={currency}
            bookedRooms={bookedRooms}
            bookedActivities={bookedActivities}
            bookedSlots={bookedSlots}
            totalAmount={totalAmount}
            cancellationPolicy={cancellationPolicy}
            onRemoveRoom={onRemoveRoom}
            onRemoveActivity={onRemoveActivity}
            onRemoveSlot={onRemoveSlot}
            onBookNow={onBookNow}
          />
        </ErrorBoundary>
      </aside>
      {/* sm 이하: 플로팅 버튼 + Drawer */}
      <aside aria-label='장바구니' className='lg:hidden'>
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          {!drawerOpen && (
            <DrawerTrigger asChild>
              <button
                className='fixed bottom-24 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#1e78ff] text-white shadow-lg shadow-black/10 transition-all hover:bg-[#0052CC] focus:outline-none max-sm:h-11 max-sm:w-11'
                aria-label='장바구니 열기'
              >
                <ShoppingCart className='h-8 w-8 max-sm:h-6 max-sm:w-6' />
                {cartCount > 0 && (
                  <span className='absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white shadow max-sm:h-5 max-sm:w-5'>
                    {cartCount}
                  </span>
                )}
              </button>
            </DrawerTrigger>
          )}
          <DrawerTitle className='invisible hidden'></DrawerTitle>
          <DrawerDescription className='invisible hidden'></DrawerDescription>
          <DrawerContent className='max-h-[90vh] rounded-t-3xl p-6'>
            <div className='mb-4 flex items-center justify-between'>
              <h2 className='text-2xl font-bold'>{t('cart')}</h2>
            </div>
            <ErrorBoundary FallbackComponent={CartFallbackComponentMobile}>
              <CartContent
                currency={currency}
                bookedRooms={bookedRooms}
                bookedActivities={bookedActivities}
                bookedSlots={bookedSlots}
                totalAmount={totalAmount}
                cancellationPolicy={cancellationPolicy}
                onRemoveRoom={onRemoveRoom}
                onRemoveActivity={onRemoveActivity}
                onRemoveSlot={onRemoveSlot}
                onBookNow={() => {
                  setDrawerOpen(false);
                  setTimeout(() => {
                    onBookNow();
                  }, 300);
                }}
              />
            </ErrorBoundary>
          </DrawerContent>
        </Drawer>
      </aside>
    </>
  );
}

const CartFallbackComponentDesktop = ({ resetErrorBoundary }: FallbackProps) => {
  const { t } = useT('product');
  const resetStore = useProductStore(state => state.resetStore);

  const handleResetAndReload = (): void => {
    resetStore();
    resetErrorBoundary();
  };

  return (
    <div className='flex h-80 flex-col items-center justify-center gap-4 rounded-3xl border border-[#E9ECEF] bg-white p-8 text-center'>
      <div className='flex h-16 w-16 items-center justify-center rounded-full bg-red-100'>
        <AlertCircle className='h-8 w-8 text-red-600' />
      </div>
      <h3 className='text-lg font-bold text-[#2E2E2E]'>
        {t('errorOccurred') || '오류가 발생했습니다'}
      </h3>
      <Button
        onClick={handleResetAndReload}
        className='rounded-2xl bg-[#0064FF] px-6 py-3 text-base font-bold text-white hover:bg-[#0052CC]'
      >
        <RefreshCw className='mr-2 h-4 w-4' />
        {t('reload') || '새로고침'}
      </Button>
    </div>
  );
};

const CartFallbackComponentMobile = ({ resetErrorBoundary }: FallbackProps) => {
  const { t } = useT('product');
  const resetStore = useProductStore(state => state.resetStore);

  const handleResetAndReload = () => {
    resetStore();
    resetErrorBoundary();
  };

  return (
    <div className='flex h-80 flex-col items-center justify-center gap-4 rounded-t-3xl border border-[#E9ECEF] bg-white p-8 text-center'>
      <div className='flex h-16 w-16 items-center justify-center rounded-full bg-red-100'>
        <AlertCircle className='h-8 w-8 text-red-600' />
      </div>
      <h3 className='text-lg font-bold text-[#2E2E2E]'>
        {t('errorOccurred') || '오류가 발생했습니다'}
      </h3>
      <Button
        onClick={handleResetAndReload}
        className='rounded-2xl bg-[#0064FF] px-6 py-3 text-base font-bold text-white hover:bg-[#0052CC]'
      >
        <RefreshCw className='mr-2 h-4 w-4' />
        {t('reload') || '새로고침'}
      </Button>
    </div>
  );
};
